import { useCallback, useEffect, useState, type ReactElement, type ReactNode } from 'react'

import { Modal, type TModalProps } from '@/src/elements/modal/Modal'
import { useLocalityAutocompleteQuery } from '@/src/features/geo/services/useLocalityAutocompleteQuery'
import { usePlaceDetailsQuery } from '@/src/features/geo/services/usePlaceDetailsQuery'
import type { TLocation } from '@/src/features/settings/api/types/editAccountSettings.types'
import { ICON } from '@/src/general/enums/icons'
import { parseApiError } from '@/src/general/utilities/fetcher/parseApiError'
import { useTranslate } from '@/src/lang/useTranslate'

import { Input } from '../Input'
import { LocalitiesList } from './LocalitiesList'

export type TLocalityModalInputProps = Omit<TModalProps, 'showModal'> & {
  placeholder: string
  inputTestId: string
  onUncaughtSubmit: (location?: TLocation) => Promise<void>
  renderTrigger: (triggerProp: { onTrigger: () => void }) => ReactElement
}

export const LocalityModalInput = ({
  onUncaughtSubmit,
  onCancel,
  placeholder,
  renderTrigger,
  inputTestId,
  ...otherProps
}: TLocalityModalInputProps): ReactNode => {
  const [showModal, setShowModal] = useState(false)
  const [value, setValue] = useState('')
  const [placeId, setPlaceId] = useState<string | undefined>()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<unknown>()
  const { t } = useTranslate()

  const localityAutocompleteQuery = useLocalityAutocompleteQuery(value.length > 1 ? value : undefined)
  const placeDetailsQuery = usePlaceDetailsQuery(placeId)

  const onClose = useCallback(() => {
    setShowModal(false)
    setValue('')
    setPlaceId(undefined)
  }, [])

  useEffect(() => {
    if (placeDetailsQuery.data && placeDetailsQuery.isSuccess && !isSubmitting) {
      setIsSubmitting(true)
      onUncaughtSubmit(placeDetailsQuery.data)
        .then(onClose)
        .catch((err) => setError(err))
        .finally(() => {
          setIsSubmitting(false)
        })
    }
  }, [
    placeDetailsQuery.data,
    onUncaughtSubmit,
    onClose,
    setIsSubmitting,
    setError,
    placeDetailsQuery.isSuccess,
    isSubmitting,
  ])

  const items = localityAutocompleteQuery.data?.suggestions || []

  return (
    <>
      <Modal
        classNames={{ body: 'pt-2 h-96', header: 'mb-0' }}
        showModal={showModal}
        onCancel={() => {
          onCancel?.()
          onClose()
        }}
        scrollable
        {...otherProps}
      >
        <Input
          inBottomSheet
          testID={inputTestId}
          placeholder={placeholder}
          value={value}
          onChange={(e) => setValue(e.nativeEvent.text)}
          errorMessage={error ? t(parseApiError(error, 'locality') || 'common.error.unknown') : undefined}
          endIcon={ICON.MARKER_PIN}
        />
        <LocalitiesList
          currentPlaceId={placeId}
          isListLoading={localityAutocompleteQuery.isLoading}
          isPlaceLoading={placeDetailsQuery.isLoading || isSubmitting}
          items={items}
          onSelect={setPlaceId}
        />
      </Modal>
      {renderTrigger({ onTrigger: () => setShowModal(true) })}
    </>
  )
}
